import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector, IState } from 'appRedux/createStore';
import { useDispatch, useSelector } from 'react-redux';
import { SlotCompareReduxCommandCreator } from 'appRedux/actions/slotCompareCommandCreator';
import { FilterReduxCommandCreator } from 'appRedux/actions/filterCommandCreator';
import { OrderByEnum, TimeFrameValueEnum } from 'common/common';
import { config } from 'config/config';
import { FilterTypeEnum } from 'common/common';
import { TimeFrameValue } from 'common/types';
import { playerTypeEnum } from 'common/commonEnum';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { useHistory, useLocation } from 'react-router';
import { INSIGHT_TIMEFRAMES } from 'config/insightTimeframes';
import { isInsightCasinoSchema } from 'services/insightCasino';

const descriptions: { [key in string]?: string } = {
  hours1: 'Last 1 Hour',
  hours4: 'Last 4 Hours',
  hours8: 'Last 8 Hours',
  hours24: 'Last 24 Hours',
  weeks1: 'Last 7 Days',
  months1: 'Last 30 Days',
};

const getPrefixForTimeframe = (value: string) => {
  switch (value) {
    case TimeFrameValueEnum.months1:
      return 'm_';
    case TimeFrameValueEnum.weeks1:
      return 'w_';
    case TimeFrameValueEnum.hours24:
      return 'd_';
    case TimeFrameValueEnum.hours8:
      return 'h8_';
    case TimeFrameValueEnum.hours4:
      return 'h4_';
    case TimeFrameValueEnum.hours1:
      return 'h1_';
    case TimeFrameValueEnum.sinceLastJackpot:
      return 'slj_';
    default:
      return '';
  }
};

interface TimeFrameInlineProps {
  selectedTimeframeValue?: string;
  selectedInsightValue?: string;
  onSelect?: () => void;
}

const TimeFrameInline: React.FC<TimeFrameInlineProps> = ({
  selectedTimeframeValue,
  selectedInsightValue,
  onSelect,
}) => {
  const dispatch = useDispatch();
  const slotCompareCommands = SlotCompareReduxCommandCreator(dispatch);
  const filterCommandCreator = FilterReduxCommandCreator(dispatch);
  const { timeFramePrefix, timeFrameOrderBy } = useAppSelector(
    (state) => state.app.persistedState.compareSlotsState
  );
  const history = useHistory();
  const location = useLocation();
  const isProUser = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.isProUser
  ) as boolean;
  const playerType = isProUser ? playerTypeEnum.slotCheckPro : playerTypeEnum.slotCheck;
  // Insight casinos (casino object has insightInstance) get their own list.
  const isInsight = useSelector((state: IState) =>
    isInsightCasinoSchema(state.app.persistedState.casinoSchema)
  );




  const allowedTimeframes = useMemo(() => {
    if (isInsight) {
      // 30d .. 1h, matching the existing drawer order; no current-hour option.
      return INSIGHT_TIMEFRAMES.slice()
        .reverse()
        .map((tf) => {
          const base = (config.timeframes || []).find((o) => o.value === tf.value);
          const isAllowed = base?.playerTypeAccess?.includes(playerType) ?? true;
          return {
            ...(base || {}),
            id: tf.id,
            value: tf.value,
            label: tf.label,
            label2: tf.label,
            description: tf.description,
            locked: !isAllowed,
          };
        });
    }
    const combinedTimeFrames = [...(config.timeframes || [])].sort((a, b) => {
      // SLJ always pinned last
      const aIsSLJ = a.value === TimeFrameValueEnum.sinceLastJackpot;
      const bIsSLJ = b.value === TimeFrameValueEnum.sinceLastJackpot;
      if (aIsSLJ && !bIsSLJ) return 1;
      if (!aIsSLJ && bIsSLJ) return -1;
      // Descending display_order so 30 days appears first, down to 1 hr
      return Number(b.display_order) - Number(a.display_order);
    });



    // Hide SLJ from the drawer's DATE RANGE section to shorten the timeframe control.
    const filtered = combinedTimeFrames.filter(
      (option) => option.value !== TimeFrameValueEnum.sinceLastJackpot
    );

    return filtered.map((option) => {
      const descriptionOverride = descriptions[option.value as string];
      let description = descriptionOverride ?? option.description;
      if (description === 'Since Last Jackpot') description = 'SLJ';
      if (description === 'Since Last Visit') description = 'SLV';
      const isAllowed = option.playerTypeAccess?.includes(playerType) ?? true;
      return {
        ...option,
        description,
        locked: !isAllowed,
      };
    });
  }, [selectedInsightValue, playerType, isInsight]);

  const [tempTimeframe, setTempTimeframe] = useState<string>(
    selectedTimeframeValue ? getPrefixForTimeframe(selectedTimeframeValue) : timeFramePrefix
  );

  useEffect(() => {
    setTempTimeframe(
      selectedTimeframeValue ? getPrefixForTimeframe(selectedTimeframeValue) : timeFramePrefix
    );
  }, [selectedTimeframeValue, timeFramePrefix]);

  const handleTimeframeSelect = (value: string) => {
    const prefix = getPrefixForTimeframe(value);
    if (!prefix) return;
    setTempTimeframe(prefix);
    slotCompareCommands.setTimeframe({
      timeframePrefix: prefix,
      timeframeOrderBy: timeFrameOrderBy || OrderByEnum.Desc,
    });
    filterCommandCreator.SetFilterOptions(FilterTypeEnum.hottestSlots, {
      timeframe: value as TimeFrameValue,
    });
    filterCommandCreator.SetFilterOptions(FilterTypeEnum.snapfilter as FilterTypeEnum, {
      timeframe: value as TimeFrameValue,
    });
  };

  if (!allowedTimeframes || allowedTimeframes.length === 0) return null;

  return (
    <div className="settings-section">
      <div className="button-group button-group--scroll-x button-group--timeframe">
        <div className="timeframe-pill-container">
          {allowedTimeframes.map((timeframe) => {
            const prefix = getPrefixForTimeframe(timeframe.value as string);
            if (!prefix) return null;
            const isActive = tempTimeframe === prefix;
            const isLocked = (timeframe as { locked?: boolean }).locked === true;
            return (
              <button
                key={timeframe.id}
                className={`timeframe-chip ${isActive ? 'filled' : 'outline'}${isLocked ? ' locked' : ''}`}
                onClick={() => {
                  if (isLocked) {
                    history.push('/auth/stripe', { previousPage: location.pathname });
                    return;
                  }
                  handleTimeframeSelect(timeframe.value as string);
                  onSelect?.();
                }}
              >
                <span className="button-text">
                  {timeframe.label2 ?? timeframe.label ?? timeframe.description}
                </span>
                {isLocked && <FontAwesomeIcon icon={faLock} className="icon-lock" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TimeFrameInline;
